/**
 * Il pool condiviso: da qui comincia la giornata.
 *
 * **Il lavoro non si assegna: si prende.** Nessuno distribuisce le commesse fra
 * gli operatori — sarebbe una persona in piu' da interpellare per cominciare, e
 * un turno che si ferma quando quella persona non c'e'. Si sceglie una riga, e
 * il servizio garantisce che non si stia prendendo cio' che ha gia' preso un
 * altro.
 *
 * **La sessione si apre prima di entrare nella coda, non dopo.** L'ordine conta:
 * se si entrasse prima e si aprisse poi, la schermata di verifica esisterebbe
 * per un istante senza nessuno che tenga i candidati, e due operatori
 * vedrebbero gli stessi. Aprire qui significa anche scoprire **qui** che su
 * quella commessa non c'e' piu' niente di libero, cioe' prima di aver cambiato
 * schermata.
 *
 * **Lo stato della lista sta nell'indirizzo.** Filtro, ordine e pagina arrivano
 * dalla route e ci tornano: un collegamento incollato in una segnalazione deve
 * riaprire cio' che chi l'ha scritta stava guardando.
 */
import { useEffect, useState } from 'react'

import { ServiceError } from '../api'
import { InspectionList } from '../InspectionList'
import type { InspectionListState } from '../InspectionList'
import { closeSession, heartbeat, openSession, prelease } from '../reviewSessions'
import type { ReviewSession } from '../reviewSessions'
import { HEARTBEAT_MS } from '../reviewSessions'
import { useT } from '../i18n/LocaleProvider'

export function WorkPool({
  state,
  onStateChange,
  onEnterQueue,
  onOpenJob,
}: {
  state: InspectionListState
  onStateChange: (next: InspectionListState) => void
  onEnterQueue: (inspectionId: string, reviewSessionId: string) => void
  onOpenJob: (inspectionId: string) => void
}): JSX.Element {
  const t = useT()
  const [session, setSession] = useState<ReviewSession | null>(null)
  const [contended, setContended] = useState(false)
  const [degraded, setDegraded] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const sessionId = session?.review_session_id ?? null
  // Il ritmo lo dice il servizio; il valore locale serve solo finche' non c'e'
  // una sessione da cui leggerlo.
  const every = session === null ? HEARTBEAT_MS : session.heartbeat_seconds * 1000

  useEffect(() => {
    if (sessionId === null) return
    const timer = setInterval(() => {
      // Un battito mancato non chiude niente: lo dice, e basta. La sessione
      // muore quando il servizio smette di sentirla, non quando la pagina
      // sospetta che sia successo.
      heartbeat(sessionId).then(
        () => setDegraded(false),
        () => setDegraded(true),
      )
    }, every)
    return () => clearInterval(timer)
  }, [sessionId, every])

  async function enter(inspectionId: string): Promise<void> {
    if (busy) return
    setBusy(true)
    setFailure(null)
    setContended(false)
    try {
      const opened = await openSession(inspectionId)
      setSession(opened)
      const batch = await prelease(opened.review_session_id, opened.max_active_leases)
      if (batch.leases.length === 0 && opened.active_leases === 0) {
        // Coda esaurita o tutta in mano ad altri: si resta qui. Entrare in una
        // coda vuota farebbe fare due passaggi per scoprire una cosa che si
        // sapeva gia'.
        //
        // **Ma solo se questa sessione non ha gia' niente in mano.** Una
        // sessione ripresa — stessa persona, stessa scheda, tornata dal pool
        // prima che i suoi lease scadessero — non ottiene lease *nuovi* e
        // insieme tiene i suoi: dire «qualcun altro la sta lavorando» a chi
        // e' quel qualcun altro e' cio' che si e' visto dal vivo il 2.09.2026,
        // con «1 candidati riservati» scritto una riga sopra.
        setContended(true)
        return
      }
      onEnterQueue(inspectionId, opened.review_session_id)
    } catch (error) {
      setFailure(
        error instanceof ServiceError ? error.message : t('common.unreachable'),
      )
    } finally {
      setBusy(false)
    }
  }

  async function leave(): Promise<void> {
    if (sessionId === null) return
    try {
      await closeSession(sessionId)
    } finally {
      // Anche se la chiusura non e' arrivata: di qua non c'e' piu' una
      // sessione da usare, e il servizio la fara' scadere da solo.
      setSession(null)
      setContended(false)
      setDegraded(false)
    }
  }

  const notice =
    session === null && failure === null ? null : (
      <section aria-label={t('pool.session')}>
        {failure !== null && <p role="alert">{failure}</p>}
        {session !== null && (
          <>
            <p role="status">
              {t('pool.sessionOpen', {
                n: session.active_leases,
                heartbeat: degraded
                  ? t('pool.degraded')
                  : t('pool.heartbeat', { n: session.heartbeat_seconds }),
              })}
            </p>
            {contended && <p>{t('pool.contended')}</p>}
            <button type="button" onClick={() => void leave()}>
              {t('pool.closeSession')}
            </button>
          </>
        )}
      </section>
    )

  return (
    <InspectionList
      state={state}
      onStateChange={onStateChange}
      onOpen={(inspectionId) => void enter(inspectionId)}
      onOpenJob={onOpenJob}
      notice={notice}
    />
  )
}
